import React, { useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Alert,
  useTheme,
  Chip,
  Tooltip,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FileDropzone from "./fileDropzone";
import api from "../../../../../../services/api";
import { alpha } from "@mui/system";

export const MAX_TOTAL_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB (soma de todos os anexos)

export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

export const getFileSize = (file) => {
  if (typeof file?.size === "number") return file.size;
  if (typeof file?.originalFile?.size === "number")
    return file.originalFile.size;
  if (typeof file?.tamanho === "number") return file.tamanho;
  return 0;
};

export default function AnexosCircuito({
  circuitoAnexos = [],
  setCircuitoAnexos,
  setDeletedAnexos,
  editMode = false,
  // Falso nas abas de circuito que estão montadas mas ocultas: sem isso, um
  // Ctrl+V adicionaria o mesmo anexo em todos os circuitos abertos em edição
  isActive = true,
  // Precisa ser único por circuito: todas as abas ficam montadas ao mesmo tempo
  // (invariante 5.1), então sem um id próprio o <input type="file"> de todas elas
  // colidia no DOM (mesmo id default) e o <label htmlFor> clicava sempre no
  // primeiro circuito montado — o anexo entrava sempre no circuito 1.
  inputId,
  setSnackbar = () => {},
}) {
  const theme = useTheme();

  // Separação entre novos (em memória do browser) e salvos (banco de dados)
  const newFiles = useMemo(
    () => (circuitoAnexos || []).filter((file) => file.isNew),
    [circuitoAnexos]
  );
  const savedFiles = useMemo(
    () => (circuitoAnexos || []).filter((file) => !file.isNew),
    [circuitoAnexos]
  );

  const totalSize = useMemo(
    () =>
      (circuitoAnexos || []).reduce((sum, f) => sum + getFileSize(f), 0),
    [circuitoAnexos]
  );
  const exceedsLimit = totalSize > MAX_TOTAL_SIZE_BYTES;

  const processFiles = useCallback(
    (acceptedFiles) => {
      if (!editMode) return;
      const addedFiles = acceptedFiles.map((file) => ({
        originalFile: file,
        name: file.name,
        type: file.type,
        size: file.size,
        preview: URL.createObjectURL(file),
        isPdf: file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"),
        isNew: true,
      }));

      if (addedFiles.length > 0) {
        setCircuitoAnexos((prevFiles) => [...prevFiles, ...addedFiles]);
      }
    },
    [editMode, setCircuitoAnexos]
  );

  // Captura de Ctrl+V (paste) na tela quando editMode estiver ativo,
  // apenas na aba de circuito visível (ver isActive)
  useEffect(() => {
    if (!editMode || !isActive) return undefined;

    const handlePaste = (event) => {
      const isTextInput =
        event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA";
      if (isTextInput && event.target.type !== "file") return;

      if (event.clipboardData && event.clipboardData.files.length > 0) {
        event.preventDefault();
        const pastedFiles = Array.from(event.clipboardData.files);
        processFiles(pastedFiles);
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [editMode, isActive, processFiles]);

  const handleDrop = (event) => {
    event.preventDefault();
    if (!editMode) return;
    const droppedFiles = Array.from(event.dataTransfer.files);
    processFiles(droppedFiles);
  };

  const handleInputChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    processFiles(selectedFiles);
    event.target.value = "";
  };

  const handleRemoveFile = (fileToRemove) => {
    if (!editMode) return;
    if (fileToRemove.isNew) {
      setCircuitoAnexos((prevFiles) => {
        const updatedFiles = prevFiles.filter((f) => f !== fileToRemove);
        if (fileToRemove.preview) {
          URL.revokeObjectURL(fileToRemove.preview);
        }
        return updatedFiles;
      });
    } else {
      if (setDeletedAnexos) {
        setDeletedAnexos((prev) => [...prev, fileToRemove]);
      }
      setCircuitoAnexos((prevFiles) =>
        prevFiles.filter((f) => f.id !== fileToRemove.id)
      );
    }
  };

  const handleFileClick = async (file) => {
    try {
      if (file.isNew) {
        const isImage = file.type ? file.type.startsWith("image/") : !file.isPdf;
        if (isImage) {
          const newWindow = window.open();
          if (newWindow) {
            newWindow.document.write(`
              <html>
                <head><title>${file.name || file.nome}</title></head>
                <body style="margin: 0; display: flex; justify-content: center; align-items: center; background-color: #0e0e0e; min-height: 100vh;">
                  <img src="${file.preview}" alt="${file.name || file.nome}" style="max-width: 100%; max-height: 100vh; object-fit: contain;" />
                </body>
              </html>
            `);
            newWindow.document.close();
          }
        } else {
          const link = document.createElement("a");
          link.href = file.preview;
          link.download = file.name || file.nome || "documento";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        setSnackbar({ children: "Baixando anexo...", severity: "info" });
        const response = await api.post(
          "/anexos/download",
          { id: file.id },
          { responseType: "blob" }
        );
        const blobUrl = URL.createObjectURL(response.data);
        const mimeType = file.mimeType || "";
        const fileName = file.nome || file.name || "anexo";
        const isImage = mimeType.startsWith("image/") || fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        const isPdf = mimeType === "application/pdf" || fileName.toLowerCase().endsWith(".pdf");

        if (isImage || isPdf) {
          window.open(blobUrl, "_blank");
        } else {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } catch (error) {
      console.error("Erro ao abrir ou baixar anexo:", error);
      setSnackbar({
        children: "Erro ao carregar arquivo.",
        severity: "error",
      });
    }
  };

  return (
    <Box sx={{ width: "100%", margin: "0 auto" }}>
      {editMode && (
        <>
          {exceedsLimit && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              O tamanho total dos anexos é {formatBytes(totalSize)} e excede o
              limite de {formatBytes(MAX_TOTAL_SIZE_BYTES)}. Remova arquivos até
              ficar dentro do limite para conseguir salvar.
            </Alert>
          )}

          <FileDropzone
            files={newFiles}
            inputId={inputId}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onFileChange={handleInputChange}
            onRemoveFile={handleRemoveFile}
          />
        </>
      )}

      {/* Exibição dos anexos já salvos no banco */}
      {savedFiles.length > 0 && (
        <Box sx={{ mt: editMode && newFiles.length > 0 ? 1.5 : 0, pt: editMode && newFiles.length > 0 ? 1.5 : 0, borderTop: editMode && newFiles.length > 0 ? `1px dashed ${theme.palette.divider}` : "none" }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 700, display: "block", mb: 1, textTransform: "uppercase" }}>
            Arquivos anexados ({savedFiles.length}):
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
            {savedFiles.map((file, index) => {
              const fileName = file.nome || file.name || "anexo";
              const isPdf =
                file.mimeType === "application/pdf" ||
                fileName?.toLowerCase()?.endsWith(".pdf");
              const isImg =
                file.mimeType?.startsWith("image/") ||
                fileName?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
              const sizeBytes = getFileSize(file);
              const sizeLabel =
                sizeBytes > 0
                  ? sizeBytes > 1024 * 1024
                    ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
                    : `${(sizeBytes / 1024).toFixed(1)} KB`
                  : "";
              const ext = fileName?.split(".")?.pop()?.toUpperCase() || "ARQUIVO";

              return (
                <Paper
                  key={file.id || index}
                  variant="outlined"
                  onClick={() => handleFileClick(file)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.2,
                    p: 1,
                    pr: 1.5,
                    borderRadius: "8px",
                    cursor: "pointer",
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? alpha(theme.palette.primary.main, 0.05)
                        : alpha(theme.palette.primary.main, 0.03),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? alpha(theme.palette.primary.main, 0.1)
                          : alpha(theme.palette.primary.main, 0.06),
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "6px",
                      bgcolor: alpha(theme.palette.primary.main, 0.14),
                      color: theme.palette.primary.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isPdf ? (
                      <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
                    ) : isImg ? (
                      <ImageOutlinedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Tooltip title={fileName || ""}>
                      <Typography variant="body2" sx={{ fontWeight: 700, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {fileName}
                      </Typography>
                    </Tooltip>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mt: 0.2 }}>
                      <Chip
                        label={ext}
                        size="small"
                        sx={{ height: 16, fontSize: "0.62rem", fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, borderRadius: "4px" }}
                      />
                      {sizeLabel && (
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: "0.68rem" }}>
                          {sizeLabel}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {editMode && (
                    <Chip
                      label="Remover"
                      size="small"
                      clickable
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(file);
                      }}
                      color="error"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, borderRadius: "6px", ml: 0.5 }}
                    />
                  )}
                </Paper>
              );
            })}
          </Box>
        </Box>
      )}


      {savedFiles.length === 0 && !editMode && (
        <Typography color="text.secondary" variant="body2" sx={{ fontStyle: "italic", mt: 1 }}>
          Nenhum anexo salvo para este circuito.
        </Typography>
      )}
    </Box>
  );
}
